import React from "react";
import Footer from "../FooterBn";

const Chip = ({ children }) => (
  <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm">
    {children}
  </span>
);

const Card = ({ children }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
    {children}
  </div>
);

const Section = ({ id, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24">
    <div className="mb-3 flex items-start justify-between gap-4">
      <div>
        <h2 className="text-lg font-black text-slate-900 sm:text-xl">
          {title}
        </h2>
        {subtitle ? (
          <p className="mt-1 text-sm leading-relaxed text-slate-600">
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
    <Card>{children}</Card>
  </section>
);

const RuleList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((t, i) => (
      <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
        <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-slate-300" />
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

const NumberedRules = ({ items }) => (
  <ol className="space-y-3">
    {items.map((x, i) => (
      <li key={i} className="flex gap-3 text-sm leading-relaxed text-slate-700">
        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-xs font-extrabold text-slate-700">
          {i + 1}
        </span>
        <div>
          <span className="font-bold text-slate-900">{x.title}</span>
          <span className="text-slate-700"> — {x.desc}</span>
        </div>
      </li>
    ))}
  </ol>
);

const InfoBox = ({ title, children, tone = "neutral" }) => {
  const tones = {
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
    warn: "border-amber-200 bg-amber-50 text-amber-900",
    danger: "border-rose-200 bg-rose-50 text-rose-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
  return (
    <div
      className={`rounded-2xl border p-4 text-sm leading-relaxed ${tones[tone]}`}
    >
      <div className="mb-1 font-extrabold">{title}</div>
      <div>{children}</div>
    </div>
  );
};

const SiteGuidelines = () => {
  return (
    <>
      <div className="relative isolate bg-slate-50">
        {/* subtle background */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-[-120px] h-[320px] w-[320px] -translate-x-1/2 rounded-full bg-slate-200/50 blur-3xl" />
          <div className="absolute right-[-120px] top-[180px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
          <div className="absolute left-[-120px] top-[420px] h-[320px] w-[320px] rounded-full bg-slate-200/40 blur-3xl" />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12">
          {/* Header */}
          <div className="mb-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="mb-2 flex flex-wrap gap-2">
                  <Chip>Policy</Chip>
                  <Chip>Community Standards</Chip>
                  <Chip>Bangladesh</Chip>
                </div>

                <h1 className="text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">
                  Goat Teachers – Site Guidelines
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
                  These Site Guidelines (“Guidelines”) govern the use of Goat
                  Teachers (
                  <a
                    href="https://www.goatteachers.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                  >
                    www.goatteachers.com
                  </a>
                  ), Bangladesh’s first complete student solution network. Goat
                  Teachers is owned and operated by Goat Technologies Ltd.
                  (“Goat Teachers,” “we,” “us,” or “our”). By accessing or using
                  Goat Teachers, you agree to comply with these Guidelines.
                </p>

                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Effective Date:{" "}
                    <span className="font-bold text-slate-900">
                      [ 20-12-2025 ]
                    </span>
                  </span>
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                    Last Updated:{" "}
                    <span className="font-bold text-slate-900">
                      [ 20-12-2025 ]
                    </span>
                  </span>
                </div>
              </div>

              {/* Quick Nav */}
              <div className="w-full lg:max-w-xs">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Quick Navigation
                  </div>
                  <div className="mt-3 grid gap-2 text-sm">
                    {[
                      ["overview", "1. Platform Overview"],
                      ["posting", "2. Posting on Goat Teachers"],
                      ["rating", "3. Rating Network Guidelines"],
                      ["resource", "4. Resource Network Guidelines"],
                      ["report", "5. Reporting a Problem"],
                      ["legal", "6. Legal Notice"],
                      ["rights", "7. Reservation of Rights"],
                    ].map(([id, label]) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-700 shadow-sm hover:border-slate-300"
                      >
                        <span className="font-semibold">{label}</span>
                        <span className="text-slate-400 group-hover:text-slate-700">
                          →
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6">
            {/* 1. Platform Overview */}
            <Section
              id="overview"
              title="1. Platform Overview"
              subtitle="Goat Teachers operates multiple networks. Each network has distinct rules, moderation standards, and legal implications."
            >
              <div className="grid gap-4 md:grid-cols-3">
                {[
                  {
                    title: "Goat Teachers",
                    desc: "Student-driven review and rating system for teachers and institutions.",
                  },
                  {
                    title: "Tutor Network",
                    desc: "Marketplace connecting students and tutors for tuition and coaching.",
                  },
                  {
                    title: "Resource Network",
                    desc: "Community-based academic resource sharing platform.",
                  },
                ].map((x, i) => (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="text-sm font-extrabold text-slate-900">
                      {x.title}
                    </div>
                    <div className="mt-1 text-sm leading-relaxed text-slate-700">
                      {x.desc}
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* 2. Posting on Goat Teachers */}
            <Section
              id="posting"
              title="2. Posting on Goat Teachers"
              subtitle="Rules for who can post, what can be posted, and posting limits."
            >
              <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    A. Rating Network
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-bold text-slate-900">
                      Who Can Post
                    </div>
                    <p className="mt-1 text-sm leading-relaxed text-slate-700">
                      Only students who are currently enrolled or were
                      previously enrolled under a specific teacher or
                      institution may submit reviews.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-bold text-slate-900">
                      What Students Can Post
                    </div>
                    <RuleList
                      items={[
                        "Feedback about universities, schools, and colleges",
                        "Reviews of teachers and their teaching methodologies",
                      ]}
                    />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-bold text-slate-900">
                      Posting Limits
                    </div>
                    <RuleList
                      items={[
                        "Each student may submit only one (1) review per teacher",
                        "Each user account may review only one (1) institute",
                        "Multiple institute reviews from a single account are strictly prohibited",
                        "Attempts to bypass these limits may result in review removal or account suspension",
                      ]}
                    />
                  </div>

                  <InfoBox title="Important Disclaimer" tone="warn">
                    Goat Teachers is not a reporting platform for illegal,
                    unsafe, or criminal activity. Such matters must be reported
                    directly to university/institutional authorities or law
                    enforcement agencies of Bangladesh.
                  </InfoBox>
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    B. Moderation Approach – Rating Network
                  </div>

                  <p className="text-sm leading-relaxed text-slate-700">
                    Goat Teachers employs a neutral, consistent, and
                    policy-driven moderation process:
                  </p>

                  <RuleList
                    items={[
                      "Reviewing flagged content",
                      "Removing guideline-violating material",
                      "Restoring content if found compliant",
                    ]}
                  />

                  <InfoBox title="Reporting" tone="neutral">
                    Users may report content using the 🚩 “Red Flag” option.
                    Moderation decisions are final and made in good faith.
                  </InfoBox>
                </div>
              </div>
            </Section>

            {/* 3. Rating Network Guidelines */}
            <Section
              id="rating"
              title="3. Goat Teachers Guidelines"
              subtitle="How to write helpful reviews, and what content will be removed."
            >
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-3">
                  <div className="text-sm font-extrabold text-slate-900">
                    Guidelines for Student Reviews
                  </div>
                  <NumberedRules
                    items={[
                      {
                        title: "Honesty Required",
                        desc: "Reviews must reflect genuine personal experience.",
                      },
                      {
                        title: "Balanced Perspective",
                        desc: "Mention strengths and weaknesses when possible.",
                      },
                      {
                        title: "Academic Focus",
                        desc: "Teaching quality, clarity, support, and learning experience only.",
                      },
                      {
                        title: "No Personal Attacks",
                        desc: "No comments on appearance, religion, gender, or beliefs.",
                      },
                      {
                        title: "Individual Opinion Only",
                        desc: "Do not speak on behalf of others.",
                      },
                      {
                        title: "No Review Wars",
                        desc: "Do not reply to or provoke other reviewers.",
                      },
                      {
                        title: "Constructive Criticism",
                        desc: "Explain why something didn’t work.",
                      },
                      {
                        title: "Respectful Language",
                        desc: "No rage, harassment, or hostility.",
                      },
                      {
                        title: "No Spam or Duplication",
                        desc: "One review per teacher.",
                      },
                      {
                        title: "User Responsibility",
                        desc: "Cross-check multiple reviews before making decisions.",
                      },
                    ]}
                  />
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Content That Will Be Removed (Rating Network)
                  </div>

                  <RuleList
                    items={[
                      "Vulgar, profane, or abusive language",
                      "Hate speech (religious, racial, gender-based, or political)",
                      "Personal contact information (phone numbers, emails, addresses)",
                      "Comments on personal or family life",
                      "Defamatory or unsubstantiated allegations",
                      "Reviews in languages other than Bangla or English",
                      "Promotional content, spam, or advertisements",
                    ]}
                  />

                  <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-extrabold text-slate-900">
                      Guidelines for Institutions and Teachers
                    </div>
                    <div className="mt-2">
                      <RuleList
                        items={[
                          "Reviewer anonymity is preserved where selected",
                          "Profiles are community-generated",
                          "Negative reviews are not removed unless rules are violated",
                          "Teachers and institutions must not rate or review themselves",
                          "In cases of spam or impersonation, contact: support@goatteachers.com",
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* 5. Resource Network Guidelines */}
            <Section
              id="resource"
              title="5. Resource Network Guidelines"
              subtitle="Rules for sharing materials and how copyright / integrity violations are handled."
            >
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Posting Rules
                  </div>
                  <RuleList
                    items={[
                      "Only educational and academic materials allowed",
                      "Users must own or have the right to share content",
                      "Pirated, copyrighted, or illegal materials are prohibited",
                    ]}
                  />
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    Moderation – Resource Network
                  </div>
                  <RuleList
                    items={[
                      "Content may be removed without notice if it violates copyright laws, academic integrity, or platform policies",
                      "Repeat violations may result in permanent access restrictions",
                    ]}
                  />
                </div>
              </div>
            </Section>

            {/* 6. Reporting */}
            <Section
              id="report"
              title="6. Reporting a Problem"
              subtitle="How to report guideline violations."
            >
              <InfoBox title="Contact Support" tone="neutral">
                Any user may report violations by emailing{" "}
                <a
                  href="mailto:support@goatteachers.com"
                  className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                >
                  support@goatteachers.com
                </a>
                . Reports are reviewed internally, and decisions are final.
              </InfoBox>
            </Section>

            {/* 7. Legal */}
            <Section
              id="legal"
              title="7. Legal Notice"
              subtitle="Neutral platform disclaimer and applicable Bangladesh law."
            >
              <div className="space-y-4 text-sm leading-relaxed text-slate-700">
                <p>
                  Goat Teachers is a neutral technology platform and does not
                  verify, endorse, or guarantee user-generated content, tutor
                  services, reviews, or resources.
                </p>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-extrabold text-slate-900">
                    User Responsibility
                  </div>
                  <p className="mt-1">
                    Users are solely responsible for their content under the
                    Digital Security Act, 2018, the ICT Act, and other
                    applicable laws of Bangladesh.
                  </p>
                </div>
                <p>
                  Goat Teachers may disclose user data to law enforcement or
                  regulatory authorities when legally required. All disputes,
                  claims, or legal proceedings shall be governed by the laws of
                  the People’s Republic of Bangladesh, with jurisdiction
                  exclusively in Bangladesh.
                </p>
              </div>
            </Section>

            {/* 8. Reservation of Rights */}
            <Section
              id="rights"
              title="8. Reservation of Rights"
              subtitle="Enforcement actions and compliance measures."
            >
              <div className="space-y-4">
                <div className="text-sm font-extrabold text-slate-900">
                  Goat Teachers reserves the right to:
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-bold text-slate-900">
                      Remove or restrict access
                    </div>
                    <RuleList
                      items={[
                        "Harmful, offensive, or misleading content",
                        "Impersonation of another person or institution",
                        "Violations of these Guidelines or Bangladeshi law",
                      ]}
                    />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-sm font-bold text-slate-900">
                      Enforcement & Disclosure
                    </div>
                    <RuleList
                      items={[
                        "Suspend or terminate accounts without prior notice",
                        "Share IP addresses, logs, and user data with authorities when required for legal compliance, user safety, or fraud prevention",
                      ]}
                    />
                  </div>
                </div>

                <InfoBox title="Good Faith Enforcement" tone="neutral">
                  Actions may be taken without prior notice to maintain platform
                  safety, trust, and compliance.
                </InfoBox>
              </div>
            </Section>

            {/* Footer note */}
            <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm">
              <div className="font-extrabold text-slate-900">Need help?</div>
              <p className="mt-1 leading-relaxed">
                Contact{" "}
                <a
                  href="mailto:support@goatteachers.com"
                  className="font-bold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-900"
                >
                  support@goatteachers.com
                </a>{" "}
                for policy questions, spam/impersonation reports, or moderation
                appeals.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default SiteGuidelines;
